import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { projects as allProjects } from '../mock';
import ProjectModal from './ProjectModal';

const Projects = () => {
  const navigate = useNavigate();
  const [expandedCategory, setExpandedCategory] = useState(null);
  // Open the project modal in place on the homepage instead of routing
  // off to /projects/<category>?project=<id> — that way, closing the
  // modal returns the user to the homepage (Featured Work section) they
  // came from, not to the category list page.
  const [selectedProject, setSelectedProject] = useState(null);

  // Reorganize by FUNCTION (Story → Production → Audience) instead of INDUSTRY
  const functionalClusters = [
    {
      id: 1,
      title: "Narrative Development & Storytelling",
      subtitle: "Shaping stories from concept to execution",
      description: "Script development, creative guidance, and concept creation across film, TV, and live experiences",
      color: "from-midnight-blue to-midnight-blue/80",
      numberColor: "#818BB1",
      numberOutline: "#F5F7FA",
      // Projects that showcase narrative development work
      projectIds: [
        { id: 3, tag: "Film & TV" },           // PALMYRA
        { id: 20, tag: "Theatrical Show" },   // LEILA
        { id: 6, tag: "Film & TV" }           // CINEMOZ
      ],
      viewAllLink: "/projects"
    },
    {
      id: 2,
      title: "Creative Production & Execution",
      subtitle: "Bringing stories to life across formats",
      description: "End-to-end production for film, television, sports ceremonies, and theatrical experiences",
      color: "from-whale-blue to-whale-blue/82",
      numberColor: "#D4AF37",
      numberOutline: "#FFFFFF",
      // Projects that showcase production/execution
      projectIds: [
        { id: 11, tag: "Sports Ceremony" },        // UEFA Champions League 2016
        { id: 18, tag: "National Celebration" },   // UAE 45th National Day
        { id: 1, tag: "Film & TV" }                // DEAD DOG
      ],
      viewAllLink: "/projects"
    },
    {
      id: 3,
      title: "Audience Strategy & Programming",
      subtitle: "Designing how stories reach and resonate",
      description: "Festival programming, content curation, and strategic positioning across global markets",
      color: "from-[#818BB1] to-[#818BB1]",
      numberColor: "#872D37",
      numberOutline: "#F5DEB3",
      // Projects that showcase audience strategy work
      projectIds: [
        { id: 16, tag: "Film Market" },            // RED SEA
        { id: 17, tag: "National Celebration" },   // KSA's 89th National Day
        { id: 10, tag: "Industry Program" }        // FFFMED
      ],
      viewAllLink: "/projects"
    }
  ];

  // Get projects by IDs for functional clusters with tags
  const getProjectsByIds = (projectData) => {
    return projectData
      .map(item => {
        const project = allProjects.find(p => p.id === item.id);
        return project ? { ...project, customTag: item.tag } : null;
      })
      .filter(p => p !== null);
  };

  return (
    <section id="projects" className="py-12 lg:py-20 bg-terracotta relative overflow-hidden">
      {/* Colorful accent */}
      <div className="absolute top-0 left-0 w-1/4 h-full bg-gradient-to-r from-terracotta/7 to-transparent"></div>
      
      <div className="container mx-auto px-8 md:px-16 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Section header — small bold centered label */}
          <div className="text-center mb-14">
            <h2 className="text-[20px] tracking-[0.2em] uppercase text-midnight-blue font-montserrat font-bold">
              Featured Work
            </h2>
          </div>

          {/* Functional clusters */}
          <div className="space-y-16">
            {functionalClusters.map((cluster, index) => {
              const clusterProjects = getProjectsByIds(cluster.projectIds);
              
              return (
                <div key={cluster.id} className="space-y-6">
                  {/* Cluster Header */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 mb-8 items-start sm:items-baseline">
                    <span
                      className="font-playfair font-bold text-5xl sm:text-6xl flex-shrink-0"
                      style={{
                        color: '#872D37',
                        letterSpacing: '-0.05em',
                        lineHeight: '1',
                        marginTop: '0.5px'
                      }}
                    >
                      0{index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-playfair font-bold text-2xl sm:text-3xl lg:text-4xl text-white mb-2" style={{ lineHeight: '1.05' }}>
                        {cluster.title}
                      </h3>
                      <p className="text-base text-white/85 font-medium mt-3">{cluster.description}</p>
                    </div>
                  </div>

                  {/* Projects Grid */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
                    {clusterProjects.map((project) => {
                      // Custom subtitle for specific projects
                      const displaySubtitle = project.id === 3 
                        ? "Fiction Series - Advanced Development\nArt Heist, Dramatic Thriller, True Events"
                        : project.id === 20
                        ? "Closing Show of the Riyadh Season 2020\nProduced by Balich Wonder Studios"
                        : project.subtitle;

                      // Duotone burgundy + midnight-blue treatment for all cards
                      const imageTreatment = { filter: 'grayscale(1) contrast(1.1)' };
                      
                      return (
                        <div
                          key={project.id}
                          onClick={() => setSelectedProject(project)}
                          className="group cursor-pointer bg-midnight-blue rounded-sm shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col h-full"
                        >
                          {/* Project Image with duotone treatment + inset vignette */}
                          <div className="relative h-48 overflow-hidden bg-charcoal/5">
                            <img
                              src={project.image}
                              alt={project.title}
                              loading="lazy"
                              decoding="async"
                              style={imageTreatment}
                              className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 group-hover:!filter-none ${
                                project.id === 20 ? 'object-[center_60%]' : ''
                              }`}
                            />
                            {/* Duotone color wash */}
                            <div
                              aria-hidden="true"
                              className="absolute inset-0 transition-opacity duration-500 group-hover:opacity-0"
                              style={{
                                background: 'linear-gradient(135deg, rgba(27,31,59,0.65) 0%, rgba(135,45,55,0.55) 100%)',
                                mixBlendMode: 'color',
                              }}
                            />
                            {/* Original gradient overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-midnight-blue/60 to-transparent opacity-70 group-hover:opacity-40 transition-opacity"></div>
                            {/* Inset vignette */}
                            <div
                              aria-hidden="true"
                              className="absolute inset-0 pointer-events-none"
                              style={{ boxShadow: 'inset 0 0 60px 8px rgba(0,0,0,0.45)' }}
                            />
                          </div>

                          {/* Project Info */}
                          <div className="p-6 flex flex-col flex-grow">
                            <h4 className="font-playfair font-bold text-xl text-white mb-2 group-hover:text-terracotta transition-colors">
                              {project.title}
                            </h4>
                            
                            {displaySubtitle && (
                              project.id === 20 ? (
                                <div className="text-sm text-white/75 mb-3 space-y-0.5">
                                  <div>Closing Show of the Riyadh Season 2020</div>
                                  <div>Produced by Balich Wonder Studios</div>
                                </div>
                              ) : (
                                <p className="text-sm text-white/75 mb-3 whitespace-pre-line">{displaySubtitle}</p>
                              )
                            )}
                            
                            {/* Bottom Row: pinned to bottom via mt-auto */}
                            <div className="mt-auto flex items-center justify-between pt-3">
                              <div className="flex items-center gap-2 text-terracotta font-montserrat font-semibold text-sm group-hover:gap-3 transition-all">
                                <span>View Project</span>
                                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                              </div>

                              {project.customTag && (
                                <span className="bg-white/10 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-montserrat font-semibold border border-white/20">
                                  {project.customTag}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* View All link for each cluster */}
                  <div className="mt-6 text-center">
                    <button
                      onClick={() => navigate(cluster.viewAllLink)}
                      className="inline-flex items-center gap-2 text-whale-blue hover:text-midnight-blue font-montserrat font-bold transition-colors group"
                    >
                      <span>View Full Portfolio</span>
                      <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Project Detail Modal — rendered in place so closing returns
          the user to the homepage instead of dropping them on the
          category list page. */}
      <ProjectModal
        project={selectedProject}
        onClose={() => setSelectedProject(null)}
      />
    </section>
  );
};

export default Projects;
